import { useEffect, useRef, useState } from "react";
import type { RoomMember, RoomModAction } from "@gooner/shared";
import { ConfirmDialog } from "../../control/components/ConfirmDialog";
import { Avatar } from "../../control/ui/Avatar";
import { GoldName } from "../../control/ui/Gold";
import { cx } from "../../control/ui/primitives";

/** Who the host's menu is about: a member as the roster knows them, or — off a chat line
 *  whose author has since left — just a name, which still bans. */
export interface MenuTarget {
  username: string;
  avatarUrl?: string;
  gold?: boolean;
  /** In the room right now (kick and camera-off only mean something then). */
  present: boolean;
  cam?: boolean;
}

export function targetOf(username: string, members: RoomMember[], fallback?: { avatarUrl?: string; gold?: boolean }): MenuTarget {
  const m = members.find((x) => x.username.toLowerCase() === username.toLowerCase());
  return m
    ? { username: m.username, avatarUrl: m.avatarUrl, gold: m.gold, present: true, cam: m.cam }
    : { username, avatarUrl: fallback?.avatarUrl, gold: fallback?.gold, present: false };
}

/**
 * THE HOST'S MENU on a person: Profile, Camera off (when they have one), Kick, Ban —
 * the two that put someone out behind a confirm. One drawing for every place a name
 * appears (the roster pill, a chat line): a host who sees a name should be able to act
 * on it wherever it is. Anchored under whatever opened it (`left-0 top-full`; the
 * anchor is `relative`), `align="right"` when it opens near the chat's right edge.
 */
export function RoomMemberMenu({
  target,
  onClose,
  onMod,
  align = "left",
}: {
  target: MenuTarget;
  onClose: () => void;
  onMod: (action: RoomModAction, username: string) => void;
  align?: "left" | "right";
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [confirm, setConfirm] = useState<"kick" | "ban" | null>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (confirm) return; // the confirm dialog is outside the menu on purpose
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !confirm && onClose();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, confirm]);
  const row = "flex w-full items-center px-3 py-1.5 text-left text-xs text-zinc-200 hover:bg-[var(--color-panel-2)]";
  return (
    <>
      <div
        ref={ref}
        role="menu"
        className={cx(
          "absolute top-full z-40 mt-2 w-44 overflow-hidden rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] py-1 shadow-xl",
          align === "right" ? "right-0" : "left-0",
        )}
      >
        <div className="flex items-center gap-2 px-3 py-1.5">
          <Avatar name={target.username} src={target.avatarUrl} size="sm" />
          <span className={cx("truncate text-xs", target.gold ? "text-[var(--color-accent-soft)]" : "text-zinc-100")}>
            <GoldName name={target.username} gold={target.gold} />
          </span>
        </div>
        <a href={`/u/${encodeURIComponent(target.username)}`} target="_blank" rel="noopener" role="menuitem" className={row}>
          Profile
        </a>
        {target.present && target.cam ? (
          <button type="button" role="menuitem" onClick={() => { onMod("cam_off", target.username); onClose(); }} className={row}>
            Camera off
          </button>
        ) : null}
        {target.present ? (
          <button type="button" role="menuitem" onClick={() => setConfirm("kick")} className={row}>
            Kick
          </button>
        ) : null}
        <button type="button" role="menuitem" onClick={() => setConfirm("ban")} className={cx(row, "text-red-300")}>
          Ban
        </button>
      </div>
      <ConfirmDialog
        open={confirm !== null}
        title={confirm === "ban" ? `Ban @${target.username}?` : `Kick @${target.username}?`}
        message={
          confirm === "ban"
            ? target.present
              ? "They leave now and can't come back to this room."
              : "They can't come back to this room."
            : "They leave now. They can rejoin from the link."
        }
        confirmLabel={confirm === "ban" ? "Ban" : "Kick"}
        onConfirm={() => {
          if (confirm) onMod(confirm, target.username);
          setConfirm(null);
          onClose();
        }}
        onCancel={() => {
          setConfirm(null);
          onClose();
        }}
      />
    </>
  );
}
